import { useEffect, useId, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useGuardedNavigate } from '@/hooks/useUnsavedChangesGuard';
import { DEFAULT_ENTITY_KINDS } from './entity-registry';
import { useOmniSearchResults } from './useOmniSearchResults';
import { useOmniSelection } from './useOmniSelection';
import type { OmniEntityKind, OmniNavSection } from './types';
import { Spinner } from '@/components/ui/spinner';
import { Kbd } from '@/components/Kbd';
import { isMacPlatform } from '@/lib/find-shortcut';

function useIsMac() {
  const [isMac] = useState(isMacPlatform);
  return isMac;
}

export type GlobalSearchProps = {
  sections: OmniNavSection[];
  entities?: OmniEntityKind[];
  canIncludeEntity?: (kind: OmniEntityKind) => boolean;
  className?: string;
};

/**
 * Top-bar omni-search: ⌘/Ctrl+K dialog with Pages + entity columns.
 */
export function GlobalSearch({
  sections,
  entities = DEFAULT_ENTITY_KINDS,
  canIncludeEntity,
  className,
}: GlobalSearchProps) {
  const guardedNavigate = useGuardedNavigate();
  const isMac = useIsMac();
  const isMobile = useIsMobile();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { columns, loading } = useOmniSearchResults({
    open,
    query,
    sections,
    entities,
    canIncludeEntity,
    onNavigate: (path) => {
      // Closed first, so the leave question isn't hidden behind the palette.
      setOpen(false);
      void guardedNavigate(path);
    },
  });

  const {
    colIndex,
    rowIndex,
    setColIndex,
    setRowIndex,
    resetSelection,
    ignoreHoverRef,
    onDialogKeyDown,
    onScrollerScroll,
  } = useOmniSelection(columns, open, listId);

  const openPalette = () => {
    setQuery('');
    resetSelection();
    setOpen(true);
  };

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.repeat) return;
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) {
        return;
      }
      if (event.altKey || event.shiftKey) return;
      event.preventDefault();
      if (open) {
        setOpen(false);
      } else {
        openPalette();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openPalette resets local state
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  const hasResults = columns.some((c) => c.items.length > 0);

  return (
    <div className={cn('flex justify-center', className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={openPalette}
        className="w-8 max-w-56 justify-center gap-2 px-0 font-normal text-muted-foreground sm:w-full sm:justify-start sm:px-2.5"
        aria-label="Open search"
      >
        <Search className="size-3.5 shrink-0" />
        <span className="hidden truncate sm:inline">Search…</span>
        <Kbd size="sm" className="ml-auto hidden sm:inline-flex">
          {isMac ? '⌘K' : 'Ctrl+K'}
        </Kbd>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className="top-4 left-4 right-4 flex h-[min(28rem,calc(100dvh-2rem))] w-auto max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:top-6 sm:left-6 sm:right-6 sm:max-w-none data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100"
          onKeyDown={onDialogKeyDown}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <DialogHeader className="shrink-0 gap-1 border-b px-3 py-3 text-left sm:px-4">
            <DialogTitle className="sr-only">Search</DialogTitle>
            <DialogDescription className="sr-only">
              Search pages, tasks and saved views
            </DialogDescription>
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label="Search"
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setColIndex(0);
                  setRowIndex(0);
                }}
                placeholder="Search pages, tasks, saved views…"
                className="h-10 border-0 bg-transparent pr-9 pl-9 shadow-none focus-visible:ring-0"
                autoComplete="off"
                aria-controls={listId}
                aria-autocomplete="list"
                aria-busy={loading || undefined}
                aria-activedescendant={
                  hasResults
                    ? `${listId}-c${colIndex}-r${rowIndex}`
                    : undefined
                }
              />
              {loading ? (
                <Spinner className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-muted-foreground" />
              ) : null}
            </div>
            <p className="px-1 text-xs text-muted-foreground">
              {isMobile
                ? 'Swipe columns · tap to open'
                : '↑↓ within column · ←→ switch column · Enter open'}
            </p>
          </DialogHeader>

          <div
            id={listId}
            role="listbox"
            aria-label="Search results"
            onScroll={onScrollerScroll}
            onMouseMove={() => {
              ignoreHoverRef.current = false;
            }}
            className="flex min-h-0 flex-1 snap-x snap-mandatory gap-0 overflow-x-auto overflow-y-hidden"
          >
            {!hasResults ? (
              <div
                className="flex w-full flex-col items-center justify-center gap-2 px-4 text-sm text-muted-foreground"
                role="status"
                aria-live="polite"
              >
                {loading ? (
                  <>
                    <Spinner size="md" />
                    <span>Searching…</span>
                  </>
                ) : (
                  <span>No results found.</span>
                )}
              </div>
            ) : (
              columns.map((col, cIdx) => {
                const Icon = col.icon;
                return (
                  <div
                    key={col.id}
                    id={`${listId}-col-${cIdx}`}
                    className="flex w-[min(100%,16rem)] shrink-0 snap-start flex-col border-r last:border-r-0 sm:w-60"
                  >
                    <div className="flex shrink-0 items-center gap-1.5 border-b px-3 py-2 text-xs font-medium text-muted-foreground">
                      <Icon className="size-3.5" />
                      <span className="truncate">{col.heading}</span>
                      <span className="ml-auto tabular-nums">
                        {col.items.length}
                      </span>
                    </div>
                    <ul className="min-h-0 flex-1 overflow-y-auto py-1">
                      {col.items.map((item, rIdx) => {
                        const active =
                          cIdx === colIndex && rIdx === rowIndex;
                        return (
                          <li
                            key={item.key}
                            id={`${listId}-c${cIdx}-r${rIdx}`}
                            role="option"
                            aria-selected={active}
                            className={cn(
                              'cursor-pointer px-3 py-2 text-sm',
                              active
                                ? 'bg-accent text-accent-foreground'
                                : 'hover:bg-muted/60'
                            )}
                            onMouseEnter={() => {
                              if (ignoreHoverRef.current) return;
                              setColIndex(cIdx);
                              setRowIndex(rIdx);
                            }}
                            onClick={() => item.onSelect()}
                          >
                            {item.node}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
